import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import Button from '../components/ui/Button';
import { PageLoader } from '../components/ui/Spinner';
import { useApp } from '../context/AppContext';
import { fmt, fmtDate } from '../utils/format';
import * as api from '../services/api';

export default function Clients() {
  const { state, fetchClients, doCreateClient, doUpdateClient, doDeleteClient, doDeactivateClient, doReactivateClient } = useApp();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [showAdhoc, setShowAdhoc] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [selId, setSelId]   = useState(null);
  
  // Client balance state loaded from API
  const [clientBalance, setClientBalance] = useState([]);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [hasAutoOpened, setHasAutoOpened] = useState(false);

  useEffect(() => {
    fetchClients({ include_inactive: showInactive });
  }, [showInactive, fetchClients]);

  async function handleDeactivate() {
    if (!selId || !selectedClient) return;
    if (!window.confirm(`Are you sure you want to deactivate client "${selectedClient.name}"?`)) return;
    setActionLoading(true);
    try {
      await doDeactivateClient(selId);
      await fetchClients({ include_inactive: showInactive });
    } catch (e) {
      // Error handled by AppContext
    } finally {
      setActionLoading(false);
    }
  }

  async function handleReactivate() {
    if (!selId || !selectedClient) return;
    setActionLoading(true);
    try {
      await doReactivateClient(selId);
      await fetchClients({ include_inactive: showInactive });
    } catch (e) {
      // Error handled by AppContext
    } finally {
      setActionLoading(false);
    }
  }

  useEffect(() => {
    if (!hasAutoOpened && state.clients && state.clients.length > 0) {
      setSelId(state.clients[0].id);
      setHasAutoOpened(true);
    }
  }, [state.clients, hasAutoOpened]);

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId]       = useState(null);
  const [form, setForm]           = useState({ name: '', gstin: '', phone: '', address: '', status: 'active' });
  const [saving, setSaving]       = useState(false);

  // Filter clients
  const filteredClients = useMemo(() => {
    const q = search.toLowerCase();
    return state.clients.filter(c => {
      const matchSearch = c.name.toLowerCase().includes(q) || (c.gstin || '').toLowerCase().includes(q);
      const matchAdhoc = showAdhoc || !c.is_adhoc;
      return matchSearch && matchAdhoc;
    });
  }, [state.clients, search, showAdhoc]);

  const selectedClient = useMemo(() => {
    return state.clients.find(c => c.id === selId) || null;
  }, [state.clients, selId]);

  // Outstanding invoices for selected client
  const outstandingInvoices = useMemo(() => {
    if (!selId) return [];
    return state.invoices.filter(inv => inv.client_id === selId && inv.status !== 'paid');
  }, [selId, state.invoices]);

  const outstandingTotal = useMemo(() => {
    return outstandingInvoices.reduce((sum, inv) => sum + (inv.balance_due || 0), 0);
  }, [outstandingInvoices]);

  // Load selected client's stock balances
  useEffect(() => {
    if (!selId) {
      setClientBalance([]);
      return;
    }
    let active = true;
    setBalanceLoading(true);
    api.getStockBalance(selId)
      .then(res => {
        if (active) setClientBalance(res || []);
      })
      .catch(err => {
        console.error('Failed to load client balance:', err);
      })
      .finally(() => {
        if (active) setBalanceLoading(false);
      });

    return () => { active = false; };
  }, [selId]);

  // Open modals
  function openAdd() {
    setEditId(null);
    setForm({ name: '', gstin: '', phone: '', address: '', status: 'active' });
    setShowModal(true);
  }

  function openEdit(c, e) {
    e.stopPropagation();
    setEditId(c.id);
    setForm({
      name: c.name || '',
      gstin: c.gstin || '',
      phone: c.phone || '',
      address: c.address || '',
      status: c.status || 'active',
    });
    setShowModal(true);
  }

  function closeModal() {
    setShowModal(false);
    setEditId(null);
    setForm({ name: '', gstin: '', phone: '', address: '', status: 'active' });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      if (editId) {
        await doUpdateClient(editId, form);
      } else {
        await doCreateClient(form);
      }
      closeModal();
    } catch (_) {}
    finally { setSaving(false); }
  }

  async function handleDeleteClient() {
    if (!selId || !selectedClient) return;
    if (!window.confirm(`Are you sure you want to delete client "${selectedClient.name}"? This cannot be undone.`)) return;
    
    try {
      await doDeleteClient(selId);
      setSelId(null);
    } catch (e) {
      // Error handled by AppContext toast
    }
  }

  const isFirstLoad = state.loading.clients && !state.clients.length;
  if (isFirstLoad) {
    return <Layout><PageLoader label="Loading client directory…" /></Layout>;
  }

  return (
    <Layout
      actions={
        <Button variant="primary" onClick={openAdd}>
          <i className="fa-solid fa-plus"></i> Add Client
        </Button>
      }
    >
      <div 
        className={`split-layout client-split-layout ${selId ? 'split-open' : ''}`}
        style={{
          gridTemplateColumns: selId ? '1fr 340px' : '1fr'
        }}
      >
        
        {/* ── Left Side: Client Table ── */}
        <div className="pane-left-large glass-card">
          <div className="card-header search-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0 }}>Registered Clients</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input 
                    type="checkbox" 
                    id="showAdhoc" 
                    checked={showAdhoc}
                    onChange={e => setShowAdhoc(e.target.checked)}
                    style={{ accentColor: 'var(--accent-cyan)' }}
                  />
                  <label htmlFor="showAdhoc" style={{ fontSize: 13, color: 'var(--text-secondary)', cursor: 'pointer' }}>Show walk-in (ad-hoc)</label>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input 
                    type="checkbox" 
                    id="showInactive" 
                    checked={showInactive}
                    onChange={e => setShowInactive(e.target.checked)}
                    style={{ accentColor: 'var(--accent-cyan)' }}
                  />
                  <label htmlFor="showInactive" style={{ fontSize: 13, color: 'var(--text-secondary)', cursor: 'pointer' }}>Show inactive</label>
                </div>
              </div>
            </div>
            <input
              type="text"
              placeholder="Filter clients by name or GSTIN..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="table-search-input"
            />
          </div>
          <div className="table-wrapper">
            <table className="dashboard-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>GSTIN</th>
                  <th>Phone</th>
                  <th>Address</th>
                  <th>Status</th>
                  <th className="align-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredClients.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="placeholder-text">No clients registered.</td>
                  </tr>
                ) : (
                  filteredClients.map(c => {
                    const isSelected = c.id === selId;
                    return (
                      <tr
                        key={c.id}
                        className={isSelected ? 'selected' : ''}
                        onClick={() => setSelId(c.id)}
                      >
                        <td><strong>{c.name}</strong></td>
                        <td><code>{c.gstin || 'N/A'}</code></td>
                        <td>{c.phone || 'N/A'}</td>
                        <td>{c.address || 'N/A'}</td>
                        <td>
                          <span className={`badge ${c.status === 'active' ? 'badge-success' : 'badge-outline'}`}>
                            {c.status}
                          </span>
                          {c.is_adhoc && (
                            <span className="badge badge-warning" style={{ marginLeft: 6 }}>Walk-in</span>
                          )}
                        </td>
                        <td className="align-right">
                          <button className="btn btn-outline" style={{ padding: '6px 12px' }} onClick={e => openEdit(c, e)}>
                            Audit Profile
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── Right Side: Client Profile Pane ── */}
        {selId && selectedClient && (
          <div className="pane-right-small glass-card" style={{ height: '100%', overflowY: 'auto' }}>
            <div className="client-profile-container" style={{ position: 'relative' }}>
              <button
                onClick={() => setSelId(null)}
                className="btn btn-icon-only"
                title="Close panel"
                style={{ position: 'absolute', top: 0, right: 0, color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16 }}
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
              <div className="profile-card-header">
                <div className="profile-icon">
                  <i className="fa-solid fa-industry"></i>
                </div>
                <h2>{selectedClient.name}</h2>
                <p style={{ fontFamily: 'var(--font-mono)', fontSize: 10 }}>ID: {selectedClient.id}</p>
              </div>

              <div className="profile-details-list">
                <div className="profile-detail-group">
                  <span className="detail-label">GSTIN</span>
                  <strong className="detail-value">{selectedClient.gstin || 'N/A'}</strong>
                </div>
                <div className="profile-detail-group">
                  <span className="detail-label">Phone</span>
                  <strong className="detail-value">{selectedClient.phone || 'N/A'}</strong>
                </div>
                <div className="profile-detail-group">
                  <span className="detail-label">Address</span>
                  <strong className="detail-value">{selectedClient.address || 'N/A'}</strong>
                </div>
                <div className="profile-detail-group">
                  <span className="detail-label">Status</span>
                  <strong className="detail-value" style={{ textTransform: 'capitalize' }}>{selectedClient.status}</strong>
                </div>
              </div>

              <div className="client-stock-summary">
                <h3>Inventory Sheet Balances</h3>
                <div className="client-stock-list">
                  {balanceLoading ? (
                    <p className="placeholder-text">Auditing asset balances…</p>
                  ) : clientBalance.length === 0 ? (
                    <p className="placeholder-text">No active inventory assets.</p>
                  ) : (
                    clientBalance.map((b, idx) => (
                      <div key={idx} className="client-stock-row">
                        <div>
                          <strong>{b.material}</strong>
                          <span>Size: {b.board_size} | GSM: {b.gsm}</span>
                        </div>
                        <strong>{(b.balance_sheets ?? 0).toLocaleString()} sh</strong>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Outstanding Bills */}
              <div className="client-outstanding-section">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <h3 style={{ fontSize: 14, color: 'var(--text-secondary)' }}>Outstanding Bills</h3>
                  {outstandingTotal > 0 && (
                    <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent-orange)' }}>{fmt(outstandingTotal)}</span>
                  )}
                </div>
                {outstandingInvoices.length === 0 ? (
                  <p style={{ fontSize: 12, color: 'var(--accent-green)', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <i className="fa-solid fa-circle-check"></i> No outstanding bills — fully paid!
                  </p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {outstandingInvoices.map(inv => (
                      <div 
                        key={inv.id} 
                        onClick={() => navigate('/invoices?invoice_id=' + inv.id)}
                        style={{ 
                          background: 'rgba(255,145,0,0.04)', 
                          border: '1px solid rgba(255,145,0,0.15)', 
                          borderRadius: 'var(--radius-md)', 
                          padding: '10px 12px', 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center', 
                          fontSize: 12,
                          cursor: 'pointer',
                          transition: 'transform 0.2s, background-color 0.2s, border-color 0.2s',
                        }}
                        className="outstanding-bill-item-card"
                      >
                        <div>
                          <strong style={{ color: 'var(--text-primary)' }}>{inv.inv_no}</strong>
                          <span style={{ display: 'block', color: 'var(--text-muted)', fontSize: 10, marginTop: 2 }}>{fmtDate(inv.inv_date) || inv.inv_date}</span>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <strong style={{ color: 'var(--accent-orange)' }}>{fmt(inv.total)}</strong>
                          <span style={{ display: 'block', fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', marginTop: 2 }}>{inv.status}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Delete / Deactivate / Reactivate Client Actions */}
              <div style={{ marginTop: 24, padding: '16px', borderTop: '1px solid var(--border-color)', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {selectedClient.status === 'active' ? (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleDeactivate} 
                    disabled={actionLoading}
                    style={{ width: '100%', borderColor: 'var(--accent-orange)', color: 'var(--accent-orange)' }}
                  >
                    <i className="fa-solid fa-ban"></i> {actionLoading ? 'Deactivating...' : 'Deactivate Client'}
                  </Button>
                ) : (
                  <Button 
                    variant="success" 
                    size="sm" 
                    onClick={handleReactivate} 
                    disabled={actionLoading}
                    style={{ width: '100%' }}
                  >
                    <i className="fa-solid fa-circle-check"></i> {actionLoading ? 'Reactivating...' : 'Reactivate Client'}
                  </Button>
                )}

                <Button variant="danger" size="sm" onClick={handleDeleteClient} style={{ width: '100%' }}>
                  <i className="fa-solid fa-trash-can"></i> Delete Client
                </Button>
                <p style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'center', marginTop: 8 }}>
                  Only clients with no linked invoices, workcards, or stock movements can be deleted.
                </p>
              </div>
            </div>
          </div>
        )}

      </div>

      {/* ── Client Add/Edit Modal (Overlay System) ── */}
      {showModal && (
        <div className="modal-overlay active">
          <div className="modal-card">
            <div className="modal-header">
              <h3>
                <i className="fa-solid fa-industry"></i>
                {editId ? 'Edit Client Profile' : 'Register New Client'}
              </h3>
              <button className="btn-close-modal" onClick={closeModal}>×</button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Company / Client Name *</label>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. ABC Paper Mills"
                  />
                </div>
                <div className="form-group">
                  <label>Client GSTIN *</label>
                  <input
                    type="text"
                    required
                    maxLength={15}
                    value={form.gstin}
                    onChange={e => setForm(f => ({ ...f, gstin: e.target.value.toUpperCase() }))}
                    placeholder="e.g. 33AABCA1234Z1Z5"
                    style={{ textTransform: 'uppercase' }}
                  />
                </div>
                <div className="form-group">
                  <label>Contact Phone</label>
                  <input
                    type="text"
                    value={form.phone}
                    onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                    placeholder="e.g. 9876543210"
                  />
                </div>
                <div className="form-group">
                  <label>Billing Address</label>
                  <textarea
                    rows={3}
                    value={form.address}
                    onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                    placeholder="Street, City, State, ZIP..."
                    style={{
                      background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border-color)',
                      borderRadius: 'var(--radius-md)', padding: '10px 14px', fontSize: 13, color: 'var(--text-primary)',
                      resize: 'vertical', width: '100%', outline: 'none'
                    }}
                  />
                </div>
                {editId && (
                  <div className="form-group">
                    <label>Status</label>
                    <select
                      value={form.status}
                      onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                      style={{
                        width: '100%', padding: '10px 14px', fontSize: 13,
                        background: 'var(--bg-dark)', color: 'var(--text-primary)',
                        border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)'
                      }}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                )}
              </div>

              <div className="modal-footer" style={{ marginTop: 20 }}>
                <Button type="button" variant="outline" onClick={closeModal}>Cancel</Button>
                <Button type="submit" variant="primary" disabled={saving}>
                  {saving ? 'Saving…' : editId ? 'Save Changes' : 'Create Client'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

    </Layout>
  );
}
